/**
 * Round 20 Y3 -- the Resilience Score's published method, inside the
 * product.
 *
 * The card read "METHODOLOGY V0.7.0" as a label; an auditor re-performed
 * posture only by inferring a linear rescale. The version now links
 * here: the open specification the backend computes against, as the
 * backend bundles it, so the page cannot drift from the sealed score's
 * method version.
 */
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { api } from "shared/api/client";

interface ScoreMethodology {
  version: string;
  markdown: string;
}

type Block =
  | { kind: "heading"; level: number; text: string }
  | { kind: "list"; items: string[] }
  | { kind: "pre"; text: string }
  | { kind: "para"; text: string };

/** Headings, lists, fenced code and paragraphs; anything else (tables)
 *  is kept as written, in a preformatted block. */
export function parseMarkdown(md: string): Block[] {
  const blocks: Block[] = [];
  const lines = md.split("\n");
  let i = 0;
  while (i < lines.length) {
    const line = lines[i] ?? "";
    if (line.startsWith("```")) {
      const body: string[] = [];
      i += 1;
      while (i < lines.length && !(lines[i] ?? "").startsWith("```")) {
        body.push(lines[i] ?? "");
        i += 1;
      }
      blocks.push({ kind: "pre", text: body.join("\n") });
      i += 1;
      continue;
    }
    const heading = /^(#{1,4})\s+(.*)$/.exec(line);
    if (heading) {
      blocks.push({ kind: "heading", level: heading[1]!.length, text: heading[2]! });
      i += 1;
      continue;
    }
    if (/^\s*[-*]\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\s*[-*]\s+|^\s{2,}\S/.test(lines[i] ?? "")) {
        const current = lines[i] ?? "";
        if (/^\s*[-*]\s+/.test(current)) items.push(current.replace(/^\s*[-*]\s+/, ""));
        else items[items.length - 1] += " " + current.trim();
        i += 1;
      }
      blocks.push({ kind: "list", items });
      continue;
    }
    if (line.startsWith("|")) {
      const rows: string[] = [];
      while (i < lines.length && (lines[i] ?? "").startsWith("|")) {
        rows.push(lines[i] ?? "");
        i += 1;
      }
      blocks.push({ kind: "pre", text: rows.join("\n") });
      continue;
    }
    if (line.trim() === "" || line.trim() === "---") {
      i += 1;
      continue;
    }
    const para: string[] = [];
    while (
      i < lines.length &&
      (lines[i] ?? "").trim() !== "" &&
      !/^(#|```|\||\s*[-*]\s)/.test(lines[i] ?? "")
    ) {
      para.push((lines[i] ?? "").trim());
      i += 1;
    }
    blocks.push({ kind: "para", text: para.join(" ") });
  }
  return blocks;
}

/** Emphasis and code marks are dropped; the words are what matter. */
function plain(text: string): string {
  return text.replace(/\*\*|`/g, "");
}

export function ScoreMethodologyView(): JSX.Element {
  const { t } = useTranslation();
  const method = useQuery({
    queryKey: ["readiness", "score-methodology"],
    queryFn: () => api.get<ScoreMethodology>("/v1/readiness/score/methodology"),
    staleTime: Infinity,
  });
  return (
    <article data-testid="score-methodology" className="mx-auto max-w-3xl space-y-3">
      {method.isLoading && (
        <p className="text-sm text-slate-500">
          {t("screens.scoreMethodology.loading")}
        </p>
      )}
      {method.isError && (
        <p className="text-sm text-rag-red">{t("screens.scoreMethodology.error")}</p>
      )}
      {method.data &&
        parseMarkdown(method.data.markdown).map((b, n) => {
          if (b.kind === "heading") {
            const cls =
              b.level === 1
                ? "font-display text-2xl text-slate-900"
                : b.level === 2
                  ? "mt-4 text-lg font-semibold text-slate-900"
                  : "mt-3 font-semibold text-slate-800";
            return (
              <p key={n} className={cls} role="heading" aria-level={b.level}>
                {plain(b.text)}
              </p>
            );
          }
          if (b.kind === "list") {
            return (
              <ul key={n} className="list-disc space-y-1 ps-6 text-sm text-slate-700">
                {b.items.map((item, k) => (
                  <li key={k}>{plain(item)}</li>
                ))}
              </ul>
            );
          }
          if (b.kind === "pre") {
            return (
              <pre
                key={n}
                dir="ltr"
                className="overflow-x-auto rounded bg-slate-50 p-3 text-xs text-slate-700"
              >
                {b.text}
              </pre>
            );
          }
          return (
            <p key={n} className="text-sm text-slate-700">
              {plain(b.text)}
            </p>
          );
        })}
    </article>
  );
}
